<template>
  <div class="shopping-cart-page app-page">
    <el-card class="cart-card" shadow="never">
      <!-- 购物车为空时显示 -->
      <div v-if="cartItems.length === 0 && !loading" class="empty-cart">
        <el-empty description="购物车是空的，快去添加商品吧～"></el-empty>
        <el-button
          type="primary"
          @click="toProductList"
          class="go-shop-btn"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>
          去购物
        </el-button>
      </div>

      <!-- 购物车列表 -->
      <div v-if="cartItems.length > 0 && !loading">
        <el-table
          :data="cartItems"
          style="width: 100%"
          class="cart-table"
          @selection-change="handleSelectionChange"
        >
          <!-- 添加选择列 -->
          <el-table-column type="selection" width="50" class-name="selection-col"></el-table-column>

          <el-table-column label="商品信息" min-width="280">
            <template #default="scope">
              <div class="product-cell">
                <el-image
                  :src="scope.row.mainImage"
                  alt="商品图片"
                  fit="cover"
                  class="cart-product-img"
                ></el-image>
                <span class="cart-product-name">{{ scope.row.productName }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column
            label="单价"
            width="120"
            align="right"
          >
            <template #default="scope">
              <span class="cart-price">¥{{ scope.row.price.toFixed(2) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            label="数量"
            width="180"
            align="center"
          >
            <template #default="scope">
              <el-input-number
                v-model="scope.row.quantity"
                :min="1"
                :max="scope.row.stock"
                @change="handleQuantityChange(scope.row)"
                class="quantity-input"
              ></el-input-number>
            </template>
          </el-table-column>
          <el-table-column
            label="小计"
            width="130"
            align="right"
          >
            <template #default="scope">
              <span class="cart-subtotal">¥{{ (scope.row.price * scope.row.quantity).toFixed(2) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            label="操作"
            width="90"
            align="center"
          >
            <template #default="scope">
              <el-button
                type="text"
                class="remove-btn"
                @click="handleRemove(scope.row.id)"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 结算区域 -->
        <div class="checkout-bar">
          <div class="select-all">
            <el-checkbox v-model="selectAll" @change="handleSelectAll">全选</el-checkbox>
            <span class="selected-info">已选 {{ selectedCount }} 件商品</span>
          </div>
          <div class="checkout-right">
            <div class="total-amount">
              <span class="total-label">合计：</span>
              <span class="total-value">¥{{ calculateTotal().toFixed(2) }}</span>
            </div>
            <el-button
              type="primary"
              size="large"
              @click="handleCheckout"
              :disabled="selectedIds.length === 0"
              class="checkout-btn"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              结算
            </el-button>
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import cartApi from '@/api/cart'

const router = useRouter()
const cartItems = ref([])
const loading = ref(false)
const selectedIds = ref([]) // 存储选中的商品ID
const selectAll = ref(false) // 全选状态

// 获取购物车列表
const fetchCartItems = async () => {
  try {
    const userId = localStorage.getItem('userId')
    if (!userId) {
      ElMessage.warning('请先登录')
      router.push('/login')
      return
    }

    loading.value = true
    const res = await cartApi.getCartList(Number(userId))
    if (res.data.code == 200) {
      cartItems.value = res.data.data || []
    } else {
      ElMessage.error(res.data.msg || '获取购物车失败')
    }
  } catch (err) {
    console.error('获取购物车失败:', err)
    ElMessage.error('获取购物车失败，请稍后重试')
  } finally {
    loading.value = false
  }
}

// 计算总价（只计算选中的商品）
const calculateTotal = () => {
  return cartItems.value
    .filter(item => selectedIds.value.includes(item.id))
    .reduce((total, item) => {
      return total + (item.price * item.quantity)
    }, 0)
}

// 计算选中商品数量
const selectedCount = ref(0)
watch(selectedIds, () => {
  selectedCount.value = cartItems.value
    .filter(item => selectedIds.value.includes(item.id))
    .reduce((sum, item) => sum + item.quantity, 0)
}, { immediate: true })

// 数量变更
const handleQuantityChange = async (item) => {
  try {
    const userId = localStorage.getItem('userId')
    const res = await cartApi.updateQuantity({
      userId: Number(userId),
      productId: item.productId,
      quantity: item.quantity
    })
    console.log(res)
    ElMessage.success('数量已更新')
  } catch (err) {
    console.error('更新数量失败:', err)
    ElMessage.error('更新数量失败')
    // 失败时回滚
    fetchCartItems()
  }
}

// 删除商品
const handleRemove = async (cartId) => {
  try {
    const userId = localStorage.getItem('userId')
    await cartApi.removeCartItem(Number(userId), cartId)
    ElMessage.success('商品已从购物车移除')
    // 从选中列表中移除
    selectedIds.value = selectedIds.value.filter(id => id !== cartId)
    fetchCartItems() // 重新获取购物车
  } catch (err) {
    console.error('删除商品失败:', err)
    ElMessage.error('删除失败，请稍后重试')
  }
}

// 处理选择变化
const handleSelectionChange = (selection) => {
  selectedIds.value = selection.map(item => item.id)
}

// 全选处理
const handleSelectAll = (val) => {
  if (val) {
    // 全选：将所有商品ID加入选中列表
    selectedIds.value = cartItems.value.map(item => item.id)
  } else {
    // 取消全选：清空选中列表
    selectedIds.value = []
  }
}

// 结算 - 携带选中的商品ID跳转
const handleCheckout = () => {
  if (selectedIds.value.length === 0) {
    ElMessage.warning('请选择要结算的商品')
    return
  }
  // 跳转到结算页面并携带选中的商品ID
  router.push({
    path: '/order/checkout',
    query: {
      cartItemIds: selectedIds.value.join(',') // 用逗号分隔ID
    }
  })
}

// 去商品列表
const toProductList = () => {
  router.push('/product/list')
}

// 页面加载时获取购物车数据
onMounted(() => {
  fetchCartItems()
})
</script>

<style scoped>
.shopping-cart-page {
  max-width: 1200px;
  margin: 0 auto;
}

.cart-card {
  border: 1px solid var(--app-border-subtle);
  border-radius: var(--app-radius-xl);
  box-shadow: var(--app-shadow-card);
  overflow: hidden;
}

.empty-cart {
  text-align: center;
  padding: 60px 0;
}

.go-shop-btn {
  margin-top: 20px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 12px 32px !important;
  font-size: 1rem !important;
  border-radius: var(--app-radius-lg) !important;
}

/* Table styles */
.cart-table {
  border-radius: var(--app-radius-lg);
  overflow: hidden;
}

.cart-table :deep(.el-table__header th) {
  background: var(--app-bg-soft) !important;
  color: var(--app-text-secondary);
  font-weight: 600;
  font-size: 0.875rem;
}

.cart-table :deep(.el-table__body td) {
  padding: 16px 0 !important;
}

.cart-table :deep(.el-table__body tr:hover td) {
  background: var(--app-color-primary-muted) !important;
}

.product-cell {
  display: flex;
  align-items: center;
  gap: 14px;
}

.cart-product-img {
  width: 70px;
  height: 70px;
  border-radius: var(--app-radius-md);
  object-fit: cover;
  border: 1px solid var(--app-border-light);
  flex-shrink: 0;
}

.cart-product-name {
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--app-text-primary);
}

.cart-price {
  font-size: 0.9375rem;
  color: var(--app-text-secondary);
  font-weight: 500;
}

.cart-subtotal {
  font-size: 1rem;
  font-weight: 700;
  color: var(--app-color-primary);
}

.quantity-input :deep(.el-input-number__decrease),
.quantity-input :deep(.el-input-number__increase) {
  background: var(--app-bg-soft);
  border-color: var(--app-border-light);
  color: var(--app-text-secondary);
}

.quantity-input :deep(.el-input-number__decrease:hover),
.quantity-input :deep(.el-input-number__increase:hover) {
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary);
}

.quantity-input :deep(.el-input__wrapper) {
  border-color: var(--app-border-light);
}

.remove-btn {
  color: var(--app-text-muted) !important;
  font-size: 0.8125rem !important;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.remove-btn:hover {
  color: #f56c6c !important;
}

/* Checkout bar */
.checkout-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding: 16px 20px;
  background: var(--app-bg-soft);
  border-radius: var(--app-radius-lg);
  border: 1px solid var(--app-border-subtle);
}

.select-all {
  display: flex;
  align-items: center;
  gap: 16px;
}

.select-all :deep(.el-checkbox__label) {
  font-size: 0.9375rem;
  font-weight: 500;
}

.selected-info {
  font-size: 0.8125rem;
  color: var(--app-text-secondary);
}

.checkout-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

.total-amount {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.total-label {
  font-size: 0.9375rem;
  color: var(--app-text-secondary);
}

.total-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--app-color-primary);
}

.checkout-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 14px 36px !important;
  font-size: 1.0625rem !important;
  font-weight: 600 !important;
  border-radius: var(--app-radius-lg) !important;
  background: var(--app-gradient-primary) !important;
  border: none !important;
  box-shadow: 0 4px 15px rgba(255, 126, 126, 0.35) !important;
}

.checkout-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 126, 126, 0.5) !important;
}

.checkout-btn:disabled {
  opacity: 0.5;
}

@media (max-width: 768px) {
  .checkout-bar {
    flex-direction: column;
    gap: 12px;
  }

  .checkout-right {
    width: 100%;
    justify-content: space-between;
  }

  .cart-product-img {
    width: 50px;
    height: 50px;
  }
}
</style>
